import React, { useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, ChevronDownIcon, RefreshCwIcon } from 'lucide-react';
import { Order, OrderStatus, DeliveryType, ORDER_STATUSES, STATUS_CONFIG } from './types';
import { StatusBadge } from './StatusBadge';
import { CONFIG } from '../data/config';
import { Station, getStationItems, itemBelongsToStation } from './StationConfig';
interface OrdersTableProps {
  orders: Order[];
  loading: boolean;
  onViewDetail: (order: Order) => void;
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  onRefresh: () => void;
  activeStation?: Station;
}
const OPEN_STATUSES = new Set(['new', 'accepted', 'preparing', 'ready']);
export function OrdersTable({
  orders,
  loading,
  onViewDetail,
  onUpdateStatus,
  onRefresh,
  activeStation = 'caja'
}: OrdersTableProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryType | 'all'>('all');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const filteredOrders = orders.filter(order => {
    const matchesSearch = search === '' || order.order_number.toLowerCase().includes(search.toLowerCase()) || order.customer_name.toLowerCase().includes(search.toLowerCase()) || order.customer_phone.includes(search);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesDelivery = deliveryFilter === 'all' || order.delivery_type === deliveryFilter;
    return matchesSearch && matchesStatus && matchesDelivery;
  });
  // Detect tables with multiple open orders — first open order per table is "original", rest are "additional"
  const tableFirstOpenOrder = new Map<string, string>();
  // Sort by created_at ascending to find the first order per table
  const sortedByDate = [...orders].filter(o => o.table_number && OPEN_STATUSES.has(o.status)).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  sortedByDate.forEach(o => {
    if (o.table_number && !tableFirstOpenOrder.has(o.table_number)) {
      tableFirstOpenOrder.set(o.table_number, o.id);
    }
  });
  const isAdditionalOrder = (order: Order) => {
    if (!order.table_number || !OPEN_STATUSES.has(order.status)) return false;
    const firstId = tableFirstOpenOrder.get(order.table_number);
    return firstId !== undefined && firstId !== order.id;
  };
  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return 'Hoy';
    return d.toLocaleDateString('es-CR', {
      day: '2-digit',
      month: 'short'
    });
  };
  return <div className="bg-surface rounded-2xl border border-gray-800 overflow-hidden" data-id="element-334">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-800 space-y-3" data-id="element-335">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-336">
          {/* Search */}
          <div className="relative flex-1" data-id="element-337">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-338" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por # pedido, nombre o teléfono..." className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-gray-700 rounded-xl text-white text-sm placeholder-gray-500 outline-none focus:ring-2 focus:ring-dorado/50 focus:border-dorado/50" data-id="element-339" />
          </div>

          {/* Refresh */}
          <button onClick={onRefresh} disabled={loading} className="flex items-center gap-2 px-4 py-2.5 bg-surface-light border border-gray-700 rounded-xl text-gray-400 hover:text-white transition-colors text-sm" data-id="element-340">
            <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-341" />
            Actualizar
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2" data-id="element-342">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mr-1" data-id="element-343">
            <FilterIcon className="w-3.5 h-3.5" data-id="element-344" />
            Filtros:
          </div>

          {/* Status Filter */}
          <button onClick={() => setStatusFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${statusFilter === 'all' ? 'bg-white/10 border-white/20 text-white' : 'border-gray-700 text-gray-500 hover:text-gray-300'}`} data-id="element-345">
            Todos
          </button>

          {ORDER_STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          return <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${statusFilter === s ? `${cfg.bg} ${cfg.color}` : 'border-gray-700 text-gray-500 hover:text-gray-300'}`} data-id="element-346">
                {cfg.label}
              </button>;
        })}

          <div className="w-px h-6 bg-gray-700 mx-1 self-center" data-id="element-347" />

          {/* Delivery Filter */}
          <button onClick={() => setDeliveryFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${deliveryFilter === 'all' ? 'bg-white/10 border-white/20 text-white' : 'border-gray-700 text-gray-500 hover:text-gray-300'}`} data-id="element-348">
            Todos
          </button>
          <button onClick={() => setDeliveryFilter('pickup')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${deliveryFilter === 'pickup' ? 'bg-white/10 border-white/20 text-white' : 'border-gray-700 text-gray-500 hover:text-gray-300'}`} data-id="element-349">
            🏪 Retiro
          </button>
          <button onClick={() => setDeliveryFilter('express')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${deliveryFilter === 'express' ? 'bg-naranja/10 border-naranja/20 text-naranja' : 'border-gray-700 text-gray-500 hover:text-gray-300'}`} data-id="element-350">
            🛵 Express
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-351">
        {loading && orders.length === 0 ? <div className="flex items-center justify-center py-20" data-id="element-352">
            <div className="w-8 h-8 border-2 border-dorado border-t-transparent rounded-full animate-spin" data-id="element-353" />
          </div> : filteredOrders.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-gray-500" data-id="element-354">
            <SearchIcon className="w-10 h-10 mb-3 opacity-30" data-id="element-355" />
            <p className="text-sm" data-id="element-356">No se encontraron pedidos</p>
          </div> : <table className="w-full text-left" data-id="element-357">
            <thead data-id="element-358">
              <tr className="border-b border-gray-800" data-id="element-359">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-360">
                  Pedido
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-361">
                  Cliente
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-362">
                  Entrega
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell" data-id="element-363">
                  Pago
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-364">
                  Total
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-365">
                  Estado
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-366">
                  Hora
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-367">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50" data-id="element-368">
              {filteredOrders.map(order => <tr key={order.id} className="hover:bg-surface-light/50 transition-colors" data-id="element-369">
                  <td className="px-4 py-3" data-id="element-370">
                    <div className="flex items-center gap-2" data-id="element-371">
                      <span className="text-white font-mono text-sm font-semibold" data-id="element-372">
                        {order.order_number}
                      </span>
                      {order.table_number && <span className="px-1.5 py-0.5 bg-dorado/15 text-dorado text-[10px] font-bold rounded border border-dorado/25" data-id="element-373">
                          Mesa {order.table_number}
                        </span>}
                      {isAdditionalOrder(order) && <span className="px-1.5 py-0.5 bg-naranja/15 text-naranja text-[10px] font-bold rounded border border-naranja/25" data-id="element-374">
                          Adicional
                        </span>}
                    </div>
                    {/* Item preview with ingredient details */}
                    {(() => {
                const lines = order.order_text.split('\n');
                const itemBlocks: {
                  name: string;
                  details: string[];
                }[] = [];
                const itemRegex = /x\d+\s*—\s*[₡$]/;
                lines.forEach(line => {
                  if (itemRegex.test(line)) {
                    // Check station filter for non-caja
                    if (activeStation === 'caja' || itemBelongsToStation(line, activeStation)) {
                      itemBlocks.push({
                        name: line.trim(),
                        details: []
                      });
                    }
                  } else if (line.trim().startsWith('·') && itemBlocks.length > 0) {
                    itemBlocks[itemBlocks.length - 1].details.push(line.trim());
                  }
                });
                return itemBlocks.length > 0 ? <div className="mt-1.5 space-y-2" data-id="element-375">
                          {itemBlocks.map((block, i) => <div key={i} className="bg-surface-light/40 rounded-lg px-2.5 py-1.5" data-id="element-376">
                              <p className="text-xs text-white font-bold leading-tight" data-id="element-377">
                                {block.name}
                              </p>
                              {block.details.length > 0 && <div className="mt-1 space-y-0.5" data-id="element-378">
                                  {block.details.map((detail, j) => <p key={j} className="text-[11px] text-gray-300 leading-tight pl-1" data-id="element-379">
                                      {detail}
                                    </p>)}
                                </div>}
                            </div>)}
                        </div> : null;
              })()}
                  </td>
                  <td className="px-4 py-3" data-id="element-380">
                    <div data-id="element-381">
                      <p className="text-white text-sm font-medium" data-id="element-382">
                        {order.customer_name}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-383">
                        {order.customer_phone}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-384">
                    <span className={`text-xs font-medium ${order.delivery_type === 'express' ? 'text-naranja' : 'text-gray-400'}`} data-id="element-385">
                      {order.delivery_type === 'express' ? '🛵 Express' : '🏪 Retiro'}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell" data-id="element-386">
                    <span className="text-gray-400 text-sm" data-id="element-387">
                      {order.payment_method}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-388">
                    <span className="text-dorado font-bold text-sm" data-id="element-389">
                      {CONFIG.CURRENCY_SYMBOL}
                      {order.total_final.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 relative" data-id="element-390">
                    {/* Non-caja: read-only status badge */}
                    {activeStation !== 'caja' ? <StatusBadge status={order.status} size="sm" data-id="element-391" /> : <>
                        {/* Quick next-status button — Caja only */}
                        {(() => {
                  const flow: OrderStatus[] = ['new', 'accepted', 'preparing', 'ready', 'delivered'];
                  const currentIdx = flow.indexOf(order.status);
                  const nextStatus = currentIdx >= 0 && currentIdx < flow.length - 1 ? flow[currentIdx + 1] : null;
                  const nextCfg = nextStatus ? STATUS_CONFIG[nextStatus] : null;
                  return <div className="flex items-center gap-1.5" data-id="element-392">
                              {/* Current status + dropdown */}
                              <button onClick={() => setOpenDropdown(openDropdown === order.id ? null : order.id)} className="flex items-center gap-1 group" data-id="element-393">
                                <StatusBadge status={order.status} size="sm" data-id="element-394" />
                                <ChevronDownIcon className="w-3 h-3 text-gray-600 group-hover:text-gray-300 transition-colors" data-id="element-395" />
                              </button>

                              {/* Quick advance button */}
                              {nextStatus && nextCfg && <button onClick={() => onUpdateStatus(order.id, nextStatus)} className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all hover:scale-105 active:scale-95 ${nextCfg.bg} ${nextCfg.color}`} title={`Cambiar a ${nextCfg.label}`} data-id="element-396">
                                  → {nextCfg.label}
                                </button>}
                            </div>;
                })()}

                        {openDropdown === order.id && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} data-id="element-397" />
                            <div className="absolute top-full left-0 mt-1 z-20 bg-surface-light border border-gray-700 rounded-xl shadow-xl py-1 min-w-[140px]" data-id="element-398">
                              {ORDER_STATUSES.map(s => {
                      const cfg = STATUS_CONFIG[s];
                      return <button key={s} onClick={() => {
                        onUpdateStatus(order.id, s);
                        setOpenDropdown(null);
                      }} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-white/5 transition-colors ${order.status === s ? `${cfg.color} font-bold` : 'text-gray-400'}`} data-id="element-399">
                                    <span className={`w-2 h-2 rounded-full ${cfg.dot}`} data-id="element-400" />
                                    {cfg.label}
                                    {order.status === s && <span className="ml-auto text-[10px]" data-id="element-401">
                                        ✓
                                      </span>}
                                  </button>;
                    })}
                            </div>
                          </>}
                      </>}
                  </td>
                  <td className="px-4 py-3" data-id="element-402">
                    <div className="text-xs text-gray-500" data-id="element-403">
                      <span data-id="element-404">{formatDate(order.created_at)}</span>
                      <br data-id="element-405" />
                      <span className="text-gray-400" data-id="element-406">
                        {formatTime(order.created_at)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-407">
                    <button onClick={() => onViewDetail(order)} className="p-2 bg-surface-light border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 transition-colors" data-id="element-408">
                      <EyeIcon className="w-4 h-4" data-id="element-409" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-800 flex items-center justify-between" data-id="element-410">
        <p className="text-xs text-gray-500" data-id="element-411">
          {filteredOrders.length} de {orders.length} pedidos
        </p>
      </div>
    </div>;
}